// finalizeGeneratedRecipe — the post-generation pipeline that turns a
// raw model draft into the recipe we actually persist + render.
//
// Extracted from AIRecipe.cookFinal so the SAME steps run whether the
// user watches the recipe stream in the foreground OR we generate it
// in the background as a pending Recipe-Book stub (see
// src/lib/backgroundRecipeGen.js). One implementation, one behavior —
// a backgrounded recipe gets identical canonical coercion, pantry
// pairing, dietary claims, reheat enrichment, and safety/technique
// cues (internal-temp doneness, etc.) as a foregrounded one.
//
// Pure-ish: no React state. Takes the draft + the three inputs that
// were AIRecipe-scoped (the user's locked ingredient list, the cook
// priority, and whether this was a dream-mode draft) and returns the
// finished recipe object — including bakingDiagnostics when the
// technique orchestrator made corrections.

import { findIngredient, coerceRecipeCanonicalIds } from "../data/ingredients";
import { extractDietaryClaims } from "./recipePairing";
import { suggestCookInstructions } from "./suggestCookInstructions";
import { runRecipeTechniqueChecks } from "./recipeValidator";
import { applyHonestTime } from "./honestTime";

/**
 * @param {object} drafted — raw recipe object as returned by the model
 * @param {object} [opts]
 * @param {Array<object>} [opts.locked] — the user's tweaked / bound ingredient
 *        list from the sketch phase. Carries pantryItemId on rows the user
 *        bound, which we re-stamp here (the model response drops it).
 * @param {string}  [opts.priority]   — "classic" | "chefs_choice" | "pantry"
 * @param {boolean} [opts.isDreamMode]
 * @returns {Promise<object>} the finished recipe, ready to persist / render
 */
export async function finalizeGeneratedRecipe(drafted, opts = {}) {
  const locked = Array.isArray(opts.locked) ? opts.locked : [];
  const priority = opts.priority || "classic";
  const isDreamMode = !!opts.isDreamMode;

  // Coerce canonical ids on the way in. Protects the stored row from AI
  // drift ("fresh_tortillas" → "tortillas") and guarantees the pantry-
  // match loop downstream operates on real registry slugs.
  const normalized = coerceRecipeCanonicalIds(drafted);

  // Stamp draft-mode signals so the preview / cook surfaces know whether
  // to run pantry pairing. Pairing should ONLY happen when priority is
  // "pantry" (eat-through mode). Classic + dream both render as authored.
  normalized._isDreamMode = isDreamMode;
  normalized._priority = priority || "classic";
  normalized._pairingActive = priority === "pantry";

  // Re-stamp pantryItemId from the locked set. The AI response only
  // carries { item, amount, ingredientId, state? } — it drops the
  // pantryItemId that buildLockedIngredients wrote for swapped/bound
  // rows. Key by ingredientId first (tightest signal) then by
  // normalized name so brand-prefixed reformulations still find their
  // locked pantry binding.
  const pairedNorm = (s) => String(s || "").toLowerCase().trim();
  const byCanon = new Map();
  const byName = new Map();
  for (const l of locked) {
    if (!l || !l.pantryItemId) continue;
    if (l.ingredientId && !byCanon.has(l.ingredientId)) byCanon.set(l.ingredientId, l.pantryItemId);
    const n = pairedNorm(l.name);
    if (n && !byName.has(n)) byName.set(n, l.pantryItemId);
  }
  const withPantry = normalized && Array.isArray(normalized.ingredients)
    ? {
        ...normalized,
        ingredients: normalized.ingredients.map((ing) => {
          if (!ing || typeof ing !== "object") return ing;
          if (ing.pantryItemId) return ing;
          const canonHit = ing.ingredientId ? byCanon.get(ing.ingredientId) : null;
          const nameHit = canonHit ? null : byName.get(pairedNorm(ing.item || ing.name));
          const pantryItemId = canonHit || nameHit || null;
          return pantryItemId ? { ...ing, pantryItemId } : ing;
        }),
      }
    : normalized;

  // Stamp dietaryClaims onto every persisted ingredient row. Claims are
  // recipe INTENT (the AI asked for "low-carb tortilla"), not transient
  // pairing — they must survive into user_recipes so preview / CookMode
  // can warn on diet-loss later even with different pantry state.
  const claimed = withPantry && Array.isArray(withPantry.ingredients)
    ? {
        ...withPantry,
        ingredients: withPantry.ingredients.map((ing) => {
          if (!ing || typeof ing !== "object") return ing;
          if (Array.isArray(ing.dietaryClaims) && ing.dietaryClaims.length > 0) return ing;
          const { claims } = extractDietaryClaims(ing.item || ing.name || "");
          if (claims.length === 0) return ing;
          return { ...ing, dietaryClaims: claims };
        }),
      }
    : withPantry;

  // Chained reheat upgrade. generate-recipe produces a reheat block as
  // ONE field in a ~40-field schema; a second focused call through
  // suggest-cook-instructions produces dramatically richer steps. Merge
  // the focused result onto recipe.reheat. Failure is non-fatal.
  // Per-part build issues — surfaced to the user instead of swallowed,
  // so a piece that fails (reheat here; the goal image in
  // backgroundRecipeGen) is VISIBLE and the rest of the recipe survives.
  const buildIssues = [];

  let enriched = claimed;
  // ONLY enrich a reheat block the model deliberately authored. The
  // generate-recipe prompt omits `reheat` for dishes that must be eaten
  // fresh (steak, surf & turf, fried eggs) — re-adding one here told the
  // leftover flow that a fresh dish keeps, so a steak dinner sprouted
  // bogus "reheat your leftovers" instructions. Gate on the model's own
  // decision: no authored reheat block → no leftovers, full stop.
  if (claimed?.title && claimed?.reheat) {
    try {
      const { cookInstructions } = await suggestCookInstructions({
        name: claimed.title,
        category: claimed.category,
      });
      if (cookInstructions?.steps?.length > 0) {
        enriched = {
          ...claimed,
          reheat: {
            primary: cookInstructions.reheat?.primary || claimed?.reheat?.primary || null,
            steps: cookInstructions.steps,
            ...(claimed?.reheat?.note ? { note: claimed.reheat.note } : {}),
          },
        };
      }
    } catch (e) {
      // Non-fatal — keep the model's first-pass reheat, but RECORD the
      // failure so the user can see it instead of it vanishing silently.
      buildIssues.push({ part: "Reheat steps", error: String(e?.message || e).slice(0, 240), retry: "reheat" });
    }
  }

  // Recipe-level technique checks. Orchestrator runs every applicable
  // sub-validator (baking, meat doneness, frying heat, pasta water,
  // parallel steps), applies their auto-fixes silently, and returns the
  // post-fix recipe + the residual diagnostics the user needs to see.
  // applyHonestTime first: reconcile the authored prep/cook header with
  // the step/timer graph (active time only) before anything persists.
  const { recipe: workingRecipe, diagnostics: mergedDiagnostics } =
    runRecipeTechniqueChecks(applyHonestTime(enriched), { findIngredient });
  const out = mergedDiagnostics
    ? { ...workingRecipe, bakingDiagnostics: mergedDiagnostics }
    : { ...workingRecipe };
  if (buildIssues.length) out._buildIssues = buildIssues;
  return out;
}
